/**
 * Derives what a list page's visible columns require from the `/query`
 * endpoint: the minimal set of CRM property names (`deriveVisibleColumnProperties`)
 * and the `include_associations` projection (`deriveVisibleColumnAssociations`).
 * Property derivation intersects with the entity type's available schema so no
 * unknown property name is ever requested (the backend returns HTTP 400 on
 * unknown property names).
 */

import type { ColumnConfig } from '../../core/types';
import { getLookupColumnParts } from './lookupColumn';

/**
 * Properties the `object_name` cell reads for the display name and avatar:
 * `getDisplayName` reads the name fields; `CrmObjectAvatar` reads the photo,
 * domain (company favicon), and email (contact Gravatar fallback) fields.
 */
export const OBJECT_NAME_IDENTITY_PROPERTIES = [
  'ahoy_display_name',
  'ahoy_first_name',
  'ahoy_last_name',
  'ahoy_name',
  'ahoy_profile_photo_url',
  'ahoy_avatar_url',
  'ahoy_domain',
  'ahoy_email',
] as const;

/** Properties the `email_initiator` cell reads for its plain-text fallback. */
export const EMAIL_INITIATOR_PROPERTIES = ['ahoy_initiator_email'] as const;

/**
 * Derive the CRM property names required to render the given columns,
 * restricted to names present in `availablePropertyNames`.
 *
 * Returns `[]` when `availablePropertyNames` is empty (schema not loaded
 * yet), so callers can omit `properties` and preserve today's
 * all-properties fetch behavior.
 */
export function deriveVisibleColumnProperties(
  columns: ColumnConfig[],
  availablePropertyNames: ReadonlySet<string>
): string[] {
  if (availablePropertyNames.size === 0) {
    return [];
  }

  const result = new Set<string>();

  const addIfAvailable = (name: string) => {
    if (availablePropertyNames.has(name)) {
      result.add(name);
    }
  };

  for (const column of columns) {
    switch (column.kind) {
      case 'property':
        if (column.name) {
          addIfAvailable(column.name);
        }
        break;

      case 'object_name':
        OBJECT_NAME_IDENTITY_PROPERTIES.forEach(addIfAvailable);
        break;

      case 'email_initiator':
        EMAIL_INITIATOR_PROPERTIES.forEach(addIfAvailable);
        break;

      case 'association':
        break;

      case 'lookup':
        break;
    }
  }

  return Array.from(result);
}

/**
 * Derive the `include_associations` projection for the given columns'
 * `kind: 'association'` entries. Associations are opt-in on the `/query`
 * endpoint, so the list request must name every association type a visible
 * column renders (`renderColumnCell` filters `object.associations` by the
 * column's `name`). Empty `properties`: cells hydrate their target badges
 * via the CRM store (`EntityBadgeByIds`), which fetches what it needs.
 *
 * Returns `undefined` when no association columns are visible so callers omit
 * the option entirely — keeping the request (and React Query cache key)
 * identical to a plain no-associations list query.
 *
 * Used by BOTH the list page and `usePrefetchCRMList` — the two MUST derive
 * identical projections or the prefetch warms a different cache key than the
 * page reads.
 */
export function deriveVisibleColumnAssociations(
  columns: ColumnConfig[]
): Record<string, { properties: string[] }> | undefined {
  const projection: Record<string, { properties: string[] }> = {};
  for (const column of columns) {
    if (column.kind === 'association' && column.name) {
      projection[column.name] ??= { properties: [] };
      continue;
    }

    if (column.kind === 'lookup') {
      const parts = getLookupColumnParts(column);
      if (!parts) {
        continue;
      }
      const entry = (projection[parts.associationTypeName] ??= {
        properties: [],
      });
      if (!entry.properties.includes(parts.targetPropertyName)) {
        entry.properties.push(parts.targetPropertyName);
      }
    }
    if (column.kind === 'email_initiator') {
      projection['ahoy_email_thread_to_initiator'] = { properties: [] };
    }
  }

  for (const entry of Object.values(projection)) {
    entry.properties.sort();
  }

  return Object.keys(projection).length > 0 ? projection : undefined;
}
